import * as React from 'react';

import * as _ from 'lodash';
import {translate} from "react-i18next";
import {Header, Image, Popup, Step, Icon} from 'semantic-ui-react';

// redux
import {connect} from "react-redux";
import {compose} from "redux";
import {ITranslatedComponent} from "../../../../redux/reducers/generalReducer";
import {IStateProperty} from "../../../BaseColumn";

// components
import Button from '../../../Button';
import ToggleButton from '../../../ToggleButton';
import TextField from '../../../TextField';

// services
import {RoleDto, WorkflowStatusDtoType, WorkflowDtoType, KumaViewDto} from '../../../../services/Client';
import {IPSLApproval, IRowStatusProps} from '../../../../services/WorkflowUtils';
import {IVirtualizedTableCellComponentProps} from "../../VirtualizedTableInterfaces";
import {selectUserRoles} from '../../../../redux/selectors';
import {IReduxState} from '../../../../redux/IReduxState';

// helpers

interface IMapStateToProps {
    roles?: RoleDto[] | undefined;
}

interface IOwnProps extends IVirtualizedTableCellComponentProps {
    onApprove?: (newValue: IPSLApproval) => void;
    onChangeWorkflow?: () => void;

}

interface KumaWorkFlowStateProps extends IOwnProps, IMapStateToProps, ITranslatedComponent {
}

interface KumaWorkFlowStateState {
    open: boolean;
    isApproved: boolean | null;
    rejectMessage: string;
    isShowConfirmDialog: boolean;
    selectedWorkflowStep: IStateProperty | null;
}

export const wfStatusRejectedImageSrc = "./images/Steps/Maintenance(WasRejected).PNG";

export const handleStepName = (name: string | undefined, rejectReason: string, hideOverflow: boolean) => {
    if (!name) {
        return (
            <p>{'{}'}</p>
        );
    }

    return (
        <>
            <p>{name}</p>
            {rejectReason.length > 0 &&
                <p className={hideOverflow ? "workflow-state-reject-reason-overflow-hidden" : "workflow-state-reject-reason"}>
                    <Image
                        src={wfStatusRejectedImageSrc}
                        width={15}
                        height={15}
                        inline={true}
                        style={{marginRight: 5}}
                    />
                    {rejectReason}
                </p>
            }
        </>
    );
};

export const getWFStatusImageSrc = (key: any) => {
    let imageSrc: string = "";

    switch (key) {
        case WorkflowStatusDtoType.APPROVAL:
            imageSrc = "./images/Steps/Approval.PNG";
            break;
        case WorkflowStatusDtoType.APPROVED:
            imageSrc = "./images/Steps/Approved.PNG";
            break;
        case WorkflowStatusDtoType.MAINTENANCE:
            imageSrc = "./images/Steps/Maintenance.PNG";
            break;
        case WorkflowStatusDtoType.FREEZE:
            imageSrc = "./images/Steps/ManuelApproval.PNG";
            break;
        case WorkflowStatusDtoType.REJECTED:
            imageSrc = "./images/Steps/Maintenance(WasRejected).PNG";
            break;
        case WorkflowStatusDtoType.TRANSLATION:
            imageSrc = "./images/Steps/Approval.PNG";
            break;
        default:
            break;
    }

    return imageSrc;
};

export class KumaWorkFlow extends React.Component<KumaWorkFlowStateProps, KumaWorkFlowStateState> {
    constructor(props: KumaWorkFlowStateProps) {
        super(props);
        this.state = {
            open: false,
            isApproved: null,
            rejectMessage: "",
            isShowConfirmDialog: false,
            selectedWorkflowStep: null
        };
    }

    private closePopup = () => {
        this.setState({
            open: false,
            isApproved: null,
            rejectMessage: "",
            isShowConfirmDialog: false,
            selectedWorkflowStep: null
        });
    }
    private onApprove = (isApprovalButton: boolean) => {
        if (isApprovalButton) {
            this.setState({
                isApproved: this.state.isApproved === true ? null : true,
                rejectMessage: "",
            });
        } else {
            this.setState({
                isApproved: this.state.isApproved === false ? null : false,
                rejectMessage: "",
            });
        }
    }

    private onRejectWorkflowStepChange = () => {
        this.closePopup();
    }
    private onApproveRequest = (hasOptionApprovalRights: boolean, canApprove: boolean) => {
        if (hasOptionApprovalRights && this.props.onApprove && canApprove) {
            // @ts-ignore warning: approved can't be null, but canApprove verifies to not be null
            this.props.onApprove({
                rejectMessage: this.state.rejectMessage,
                // @ts-ignore warning
                approved: this.state.isApproved,
            });

            this.closePopup();
        }
    }
    private callResetWorkflow =() => {
        const { value } = this.props;
        if (this.props.onOptionWFReset) {
            // reset for option
            this.props.onOptionWFReset();
        } else {
            // reset for OB
            const currentValue = _.clone(value);
            currentValue.statusValueState = currentValue.statusValueStates[0].id;
            this.props.onChange(currentValue);
        }
    }
    private resetWorkflow = () => {
         this.callResetWorkflow();
    }
    private onAcceptWorkflowStepChange = () => {
        const currentValue = _.clone(this.props.value);
        if (this.state.selectedWorkflowStep) {
            currentValue.statusValueState = this.state.selectedWorkflowStep.id;
            this.props.onChange(currentValue);
            this.closePopup();
        }
    }

    render() {
        const value: IRowStatusProps = this.props.value;
        let canReset = value.statusValueStateResetable &&  this.props.roles && this.props.roles.find(role =>
                value.resetRoles.find(resetRole => resetRole.objectId === role.objectId) !== undefined);
        const currentStep = value.statusValueStates.find((item: IStateProperty) => item.id === value.statusValueState) as IStateProperty;

        const isSaveChangesDisabled = this.props.workflowReadOnly || this.state.isApproved === null || value.isDependentOverwriteSpecific
            || (this.state.isApproved === false && this.state.rejectMessage.trim() === "");
        let imageSrc: string = getWFStatusImageSrc(this.props.value.statusView);
        const showChangeWfHint = this.props.inEditMode && currentStep.type !== WorkflowStatusDtoType.MAINTENANCE;
        const firstRejectState = value.statusValueStates.find(state => state.rejectReason.length > 0);
        const isTranslationStepUsed = value.statusValueStates.find(state => state.type === WorkflowStatusDtoType.TRANSLATION) !== undefined
            && currentStep.type !== WorkflowStatusDtoType.APPROVAL;

        return (
            <Popup
                trigger={
                    <div>
                        <div className="text-align--center margin-top--10px">
                            {handleStepName(currentStep.hoverText, firstRejectState ? firstRejectState.rejectReason : "", true)}
                        </div>
                        <Image
                            className="margin-top--10px"
                            centered={true}
                            src={imageSrc}
                            width={30}
                            height={30}
                        />
                    </div>
                }
                flowing={true}
                hoverable={this.state.isApproved === null ? true : false}
                position={'top right'}
                open={this.state.open}
                on={this.state.isApproved === null ? 'hover' : 'click'}
                onOpen={() => this.setState({open: true})}
                onClose={this.closePopup}
                onUnmount={this.closePopup}
                hideOnScroll={true}
                className={'zIndex-9999'}
                content={
                    <>
                        {/** workflow info */}
                        <div style={{maxWidth: '1000px'}} >
                            {value.workFlowName &&

                                <div style={{marginBottom: '15px'}} className='display--flex align-items--center'>

                                    {value.hasChangeWfButton &&
                                        <div className={showChangeWfHint ? 'workflow-state-tooltip' : ''}>
                                            <Button
                                                className={'workflow-state-change-button margin--top-0'}
                                                disabled={!this.props.inEditMode || this.props.readOnly || currentStep.type !== WorkflowStatusDtoType.MAINTENANCE}
                                                onClick={this.props.onChangeWorkflow}
                                            >
                                                <Icon name={'edit'} size={'large'} />
                                            </Button>
                                            {showChangeWfHint &&
                                                <span className="tooltiptext">{this.props.t("OPTIONS_VIEW:CHANGE_WF_HINT")}</span>
                                            }
                                        </div>
                                    }
                                    <Header className={'margin--top-0'}>
                                        {value.workFlowName}
                                        <span style={{fontWeight: 'normal'}}>
                                            {" - " + (value.workflowResetPointRejection
                                                ? this.props.t("WORKFLOW:REJECT_TO_RESET_POINT_INFO")
                                                : this.props.t("WORKFLOW:REJECT_TO_START_POINT_INFO")
                                            )}
                                        </span>
                                    </Header>
                                </div>
                            }

                            <Step.Group className={"workflow-state-step-group"} size={'tiny'}>
                                {value.statusValueStates.map((item, index) => {
                                    const suppressWFChange: boolean | undefined =
                                        !this.props.readOnly
                                        && ((value.invalidTexts && item.type !== WorkflowStatusDtoType.TRANSLATION))
                                        && value.statusStepIndex !== undefined && value.statusStepIndex > -1 && value.statusStepIndex + 1 === index;

                                    const stepClasses = "workflow-state-step "
                                        + (item.id === value.statusValueState && this.props.readOnly ? "workflow-state-step-active " : "")
                                        + (suppressWFChange ? "workflow-state-tooltip" : "");

                                    return (
                                        <Step
                                            key={"step-" + index}
                                            link={true}
                                            onClick={() => {
                                                if ((!suppressWFChange || isTranslationStepUsed)) {
                                                    if (item.type === WorkflowStatusDtoType.TRANSLATION) {
                                                        this.setState({isShowConfirmDialog: true, selectedWorkflowStep: item});
                                                    } else {
                                                        const currentValue = _.clone(value);
                                                        currentValue.statusValueState = item.id;
                                                        this.props.onChange(currentValue);
                                                    }
                                                }
                                            }}
                                            disabled={this.props.readOnly}
                                            active={item.id === value.statusValueState}
                                            className={stepClasses}
                                        >
                                            <Step.Content>
                                                <Step.Title>
                                                    {item.isResetPoint && <Icon name="backward" disabled={this.props.readOnly} />}
                                                    {handleStepName(item.hoverText, item.rejectReason, false)}
                                                </Step.Title>
                                                <Step.Description>
                                                    <b>{this.props.t("GLOBAL:ROLE")}</b>: {item.role}
                                                </Step.Description>
                                                {(suppressWFChange && !isTranslationStepUsed) &&
                                                    <span className={"tooltiptext"}>{this.props.t("OPTIONS_VIEW:EMPTY_TEXTS_WARNING")}</span>
                                                }
                                            </Step.Content>
                                        </Step>
                                    );
                                })}
                            </Step.Group>
                        </div>
                        {/** actions  section */}
                        <div>
                            {/** approval */}

                            {/** reject message */}
                            {this.state.rejectMessage.trim() === ''
                                && this.state.isApproved === false
                                && !this.state.isShowConfirmDialog &&
                                <p style={{color: 'red'}}>{this.props.t('GLOBAL:APPROVAL_SAVE_HINT')}</p>
                            }

                            {/** reset */}
                            {canReset && !this.state.isShowConfirmDialog &&
                                <div>
                                    <Button
                                        disabled={this.props.workflowReadOnly}
                                        label={this.props.t("GLOBAL:RESET_WORKFLOW_BUTTON")}
                                        icon="backward"
                                        labelPosition="after"
                                        floated="right"
                                        onClick={() => this.resetWorkflow()}
                                    />
                                </div>
                            }

                            {this.state.isShowConfirmDialog &&
                                <div>

                                    <ToggleButton
                                        toggled={this.state.isShowConfirmDialog}
                                        icon="check"
                                        onClick={this.onAcceptWorkflowStepChange}
                                        className={'workflow-state-approval-btn'}
                                    />
                                    <ToggleButton
                                        toggled={!this.state.isShowConfirmDialog}
                                        icon="ban"
                                        onClick={this.onRejectWorkflowStepChange}
                                        className={'workflow-state-approval-btn'}
                                    />
                                </div>
                            }
                        </div>

                        {/** specific PSL action */}
                        {value.isDependentOverwriteSpecific && !this.state.isShowConfirmDialog &&
                            <div>
                                <Button
                                    disabled={this.props.workflowReadOnly}
                                    label={this.props.t("OPTIONS_VIEW:CREATE_IO_SPECIFIC_OPTION_BTN")}
                                    icon="file"
                                    labelPosition="after"
                                    floated="right"
                                    onClick={this.props.createIOspecificOption}
                                />
                            </div>
                        }
                    </>
                }
            />
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Table/components/kuma/workflow.tsx